var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
// #region Imports
import React, { useEffect, useState } from "react";
import { Col, Form, OverlayTrigger, Popover, Row, Spinner } from "react-bootstrap";
import { FormattedMessage, defineMessages } from "react-intl";
import { PatternFormat } from "react-number-format";
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { useAppState } from "shared/AppState/app-state";
import * as lodash from 'lodash';
import { useFormik } from "formik";
import * as helperFunctions from 'shared/utils/HelperFunctionsTS';
import { NotificationType, useAssignVendorContactToOrderMutation, useGetVendorContactsQuery, useSendVerificationEmailMutation, useResendVerificationEmailMutation, useUpdateVendorApprovalContactMutation } from "shared/types/__generated__/graphql-type";
import styled from "styled-components";
import ContactVerificationModal from "screens/PO/RepairContactView/ContactVerificationModal";
import configuration from "shared/configuration";
import * as constants from "shared/constants/App";
import { ButtonStyled } from "shared/DesignSpecs/components/FormComponents";
import FormWrapper from "shared/components/FormWrapper";
import { connect } from "react-redux";
import { event } from 'shared/services/GoogleAnalytics';
import { isSuccessfulContactMutationResult } from "shared/utils/ContactMutationUtils";
// #endregion
// #region Global variables
const phoneRegExp = /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;
let contactSchema = yup.object({
    isExistingContact: yup.boolean(), // This is a dummy field to bypass name validation when existing contact is selected
    contactName: yup.string()
        .trim()
        .when("isExistingContact", ([isExistingContact], schema) => {
        return isExistingContact ?
            schema.notRequired() :
            schema.required("service_contact__name_required")
                .matches(/^[A-z,.'-]+[A-z ,.'-]+$/, "service_contact__name_required");
    }),
    contactEmail: yup.string()
        .trim()
        .email("service_contact__email_required")
        .required("service_contact__email_required"),
    contactPhone: yup.string()
        .trim()
        .matches(phoneRegExp, "service_contact__phone_required")
        .required("service_contact__phone_required"),
    contactExt: yup.string()
        .trim()
        .optional()
        .matches(/^[0-z]*$/, "service_contact__ext_required")
});
const emptyContact = {
    id: "",
    emailAddress: "",
    name: "",
    phoneNumber: "",
    extension: "",
    vendorId: "",
    notifyApprovals: false,
    notifyPayments: false,
    type: NotificationType.Payment,
    vendorEmailIsVerified: false,
    vendorPhoneIsVerified: false
};
// #endregion
// #region Styled Components
const ContactSelect = styled(Form.Select) `
    width: 100%;
    padding: 0 .5rem 0 1rem;
    font-size: inherit;
    border-radius: 0;
    display: inline-block;
    margin-top: 20px;
    height: 25px;
    text-overflow: ellipsis;
    border: 1px solid #5587B7;
    &:focus {
        border-color: #5587B7;
        outline: inherit;
        box-shadow: inherit;
    }
`;
const ContactContainer = styled.div `
  background-color: #ffffff;
  border: 0.15em solid #dce2f3;
  padding-left: 2.5em;
  padding-right: 2.5em;
`;
const SubmitButton = styled.a `
  font-family: "Gotham SSm A", "Gotham SSm B";
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  padding-bottom: 40px;
  padding-top: 3rem;
  text-decoration: none;
  align-text: right;
  width: fit-content;
`;
// #endregion
//#region Component
const RepairContactView = (_a) => {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var props = __rest(_a, []);
    // #region State
    const [overlayContactShown, setOverlayContactShown] = useState(false);
    const [selectedContact, setSelectedContact] = useState(emptyContact);
    const [formErrors, setFormErrors] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [verificationEmailSent, setVerificationEmailSent] = useState(false);
    const [resendDisabled, setResendDisabled] = useState(false);
    const [resendTime, setResendTime] = useState(configuration.verificationEmailResendTimeInMinutes * 60);
    const [timer, setTimer] = useState(null);
    const [verificationId, setVerificationId] = useState(null);
    const [oldVendorContact, setOldVendorContact] = useState(emptyContact);
    // #endregion
    // #region Refs
    // #endregion
    //#region Hooks
    const [appState, dispatch] = useAppState();
    const { vehicleId } = useParams();
    const { data: contactsData, loading: contactsLoading, error: contactsError, refetch } = useGetVendorContactsQuery({
        variables: {
            id: props.vendorId
        },
        client: props.vendorClient,
        onError(error) {
            setFormErrors(helperFunctions.getApolloErrorMessage(error));
        },
        onCompleted() {
            setFormErrors([]);
        }
    });
    const [sendVerificationEmailMutation, { data: sendEmailData, loading: sendEmailLoading, error: sendEmailError }] = useSendVerificationEmailMutation({
        client: props.vendorClient,
        onError(error, clientOptions) {
            setFormErrors(helperFunctions.getApolloErrorMessage(error));
        },
        onCompleted() {
            setFormErrors([]);
        }
    });
    const [resendVerificationEmailMutation, { data: resendEmailData, loading: resendEmailLoading, error: resendEmailError }] = useResendVerificationEmailMutation({
        client: props.vendorClient,
        onError(error, clientOptions) {
            setFormErrors(helperFunctions.getApolloErrorMessage(error));
        },
        onCompleted() {
            setFormErrors([]);
        }
    });
    const [assignVendorContactToOrderMutation, { data: assignData, loading: assignLoading, error: assignError }] = useAssignVendorContactToOrderMutation({
        client: props.vendorClient,
        onError(error, clientOptions) {
            setFormErrors(helperFunctions.getApolloErrorMessage(error));
        },
        onCompleted() {
            setFormErrors([]);
        }
    });
    const [updateVendorApprovalContactMutation, { data: updateData, loading: updateLoading, error: updateError }] = useUpdateVendorApprovalContactMutation({
        client: props.vendorClient,
        onError(error, clientOptions) {
            setFormErrors(helperFunctions.getApolloErrorMessage(error));
        },
        onCompleted() {
            setFormErrors([]);
        }
    });
    const formik = useFormik({
        initialValues: {
            // isExistingContact: false when new contact or when existing contact is missing a name
            isExistingContact: (selectedContact.id == -1 || selectedContact.id == null || selectedContact.id == "") || (selectedContact.name == "" || selectedContact.name == null) ? false : true,
            contactName: selectedContact.name ? selectedContact.name : "",
            contactEmail: selectedContact.emailAddress ? selectedContact.emailAddress : "",
            contactPhone: selectedContact.phoneNumber ? selectedContact.phoneNumber : "",
            contactExt: selectedContact.extension ? selectedContact.extension : ""
        },
        validationSchema: contactSchema,
        validateOnChange: false,
        enableReinitialize: true,
        onSubmit: (values) => { }
    });
    //#endregion
    // #region Effects
    // This is to prevent memory leaks and ensure that the timer is cleared when the component is unmounted
    useEffect(() => {
        return () => {
            if (timer) {
                clearInterval(timer);
            }
        };
    }, []);
    // #endregion
    // #region Event handlers
    /**
     * Select event handler for contact dropdown
     * @param e Select event - Contact dropdown change event
     */
    const onSelectContact = (e) => {
        // Clear form errors
        setOverlayContactShown(false);
        setFormErrors([]);
        let contactId = e.target.value;
        // Clear sent email verification flag
        setVerificationEmailSent(false);
        setOldVendorContact(emptyContact);
        // If new contact is selected, clear form values
        if (contactId != "-1" && contactId != "") {
            let contactData = contactsData.vendor.vendorContacts.find(c => c.id == contactId);
            setSelectedContact(contactData);
            (contactData.name == "" || contactData.name == null) ? formik.setFieldValue('isExistingContact', false) : formik.setFieldValue('isExistingContact', true);
        }
        else {
            setSelectedContact(Object.assign(Object.assign({}, emptyContact), { id: contactId }));
            formik.setFieldValue('isExistingContact', false);
        }
    };
    /**
     * Validates the contact form
     * @returns Promise<boolean> - Returns true if form is valid, false otherwise
     */
    const validateContact = () => __awaiter(void 0, void 0, void 0, function* () {
        if (selectedContact.id === "" || selectedContact.id === null) {
            setOverlayContactShown(true);
            return false;
        }
        else {
            return formik.validateForm().then((formErrors) => {
                if (lodash.isEmpty(formErrors)) {
                    return true;
                }
                else
                    return false;
            }, () => { return false; });
        }
    });
    /**
     *
     * @param oldContact VendorContact - Old contact data
     * @param phoneNumber Number - Unformatted phone number
     * @returns Boolean - Returns true if contact is updated, false otherwise
     */
    const isContactUpdated = (oldContact, phoneNumber) => {
        return oldContact.name != formik.values.contactName || oldContact.emailAddress != formik.values.contactEmail || oldContact.phoneNumber != phoneNumber || oldContact.extension != (formik.values.contactExt == "" ? null : formik.values.contactExt);
    };
    /**
     * Handles the contact save event.
     */
    const handleContactSave = () => __awaiter(void 0, void 0, void 0, function* () {
        const replaceRegex = /[() ]/g;
        var phoneNumber = formik.values.contactPhone.replace(replaceRegex, "");
        // If verification email is sent and contact wasn't updated, show verification modal
        if (verificationEmailSent && !isContactUpdated(selectedContact, phoneNumber)) {
            setShowModal(true);
        }
        else {
            // If new contact, create new contact
            if (selectedContact.id == -1) {
                const newContact = Object.assign(Object.assign({}, emptyContact), { id: selectedContact.id, name: formik.values.contactName, emailAddress: formik.values.contactEmail, phoneNumber: phoneNumber, extension: formik.values.contactExt, vendorId: props.vendorId });
                setSelectedContact(newContact);
                handleContactVerification(newContact);
            }
            else {
                // Skip verification if email is verified and unchanged
                const oldContactData = contactsData.vendor.vendorContacts.find(c => c.id == selectedContact.id);
                if (isContactUpdated(oldContactData, phoneNumber)) {
                    if ((oldContactData.vendorEmailIsVerified && oldContactData.emailAddress == formik.values.contactEmail)) {
                        yield updateVendorApprovalContactMutation({
                            variables: {
                                vendorContactId: selectedContact.id,
                                name: formik.values.contactName,
                                emailAddress: selectedContact.emailAddress,
                                phoneNumber: phoneNumber,
                                extension: formik.values.contactExt
                            }
                        }).then((result) => __awaiter(void 0, void 0, void 0, function* () {
                            if (isSuccessfulContactMutationResult(result)) {
                                yield refetch().then((result) => {
                                    handleContactVerification(result.data.vendor.vendorContacts.find(c => c.id == selectedContact.id));
                                });
                            }
                        }));
                    }
                    else {
                        // Otherwise, verify email
                        const newContact = Object.assign(Object.assign({}, oldContactData), { name: formik.values.contactName, emailAddress: formik.values.contactEmail, phoneNumber: phoneNumber, extension: formik.values.contactExt, vendorEmailIsVerified: formik.values.contactEmail == oldContactData.emailAddress ? oldContactData.vendorEmailIsVerified : false, vendorPhoneIsVerified: phoneNumber == oldContactData.phoneNumber ? oldContactData.vendorPhoneIsVerified : false });
                        setSelectedContact(newContact);
                        setOldVendorContact(oldContactData);
                        handleContactVerification(newContact);
                    }
                }
                else {
                    //If unchanged existing contact, handle verification
                    handleContactVerification(selectedContact);
                }
            }
        }
    });
    /**
     * If contact is not verified, send verification email and display popup. If contact is verified, create PO
     * @param contact VendorContact - Contact data
     */
    const handleContactVerification = (contact) => __awaiter(void 0, void 0, void 0, function* () {
        //If email is not verified, send verification email
        if (!contact.vendorEmailIsVerified /* and contact type is email */) {
            yield sendVerificationEmailMutation({
                variables: {
                    emailAddress: contact.emailAddress,
                    vendorId: contact.vendorId,
                }
            }).then((result) => {
                if (result.data.sendVerificationEmail.verificationEmailPayload.success) {
                    setVerificationId(result.data.sendVerificationEmail.verificationEmailPayload.vendorContactVerifyId);
                    //Disable resend button and start resend timer
                    setVerificationEmailSent(true);
                    setResendDisabled(true);
                    startResendTimer();
                    setShowModal(true);
                }
            });
        }
        //else if(!contact.vendorPhoneIsVerified /* and contact type is phone */) {
        //}
        else {
            // Track successful PO creation event
            event({
                action: 'create_po',
                category: 'PO',
                label: 'Create PO',
                value: vehicleId,
            });
            props.dispatch({ type: constants.CREATE_PO_CLICKED, vehicleId: vehicleId, assignVendorContactToOrder: assignVendorContactToOrderMutation, vendorContactId: contact.id });
        }
    });
    /**
     * Resend verification email
     * @param contact VendorContact - Contact data
     */
    const resendVerificationEmail = (contact) => __awaiter(void 0, void 0, void 0, function* () {
        yield resendVerificationEmailMutation({
            variables: {
                vendorContactVerifyId: verificationId,
                emailAddress: contact.emailAddress,
                vendorId: contact.vendorId,
            }
        }).then((result) => {
            if (result.data.reSendVerificationEmail.verificationEmailPayload.success) {
                setVerificationId(result.data.reSendVerificationEmail.verificationEmailPayload.vendorContactVerifyId);
                //Disable resend button and start resend timer
                setResendDisabled(true);
                startResendTimer();
            }
        });
    });
    /**
     * Start verification resend timer
     */
    const startResendTimer = () => {
        if (timer)
            clearInterval(timer);
        setResendTime(configuration.verificationEmailResendTimeInMinutes * 60);
        setTimer(setInterval(() => {
            setResendTime((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(timer);
                    setResendDisabled(false);
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000));
    };
    /**
     * If contact not selected, show overlay. If contact selected, validate contact
     */
    const handleSubmissionClicked = () => {
        if (selectedContact.id === "" || selectedContact.id === null) {
            setOverlayContactShown(true);
        }
        else {
            validateContact().then((isValid) => {
                if (isValid) {
                    handleContactSave();
                }
            });
        }
    };
    // #endregion
    //#region International messages
    const messages = defineMessages({
        service_contact_name: {
            id: 'service_contact_name',
            description: 'Service Contact Name',
            defaultMessage: 'Service Contact Name'
        },
        service_contact_phone: {
            id: 'service_contact_phone',
            description: 'Service Contact Phone',
            defaultMessage: 'Service Contact Phone'
        },
        service_contact_ext: {
            id: 'service_contact_ext',
            description: 'Ext.',
            defaultMessage: 'Ext.'
        },
        service_contact_email: {
            id: 'service_contact_email',
            description: 'Service Contact Email',
            defaultMessage: 'Service Contact Email'
        },
        service_contact_new_contact: {
            id: 'service_contact_new_contact',
            description: 'Create New Contact',
            defaultMessage: 'Create New Contact'
        },
        service_contact_new_contact_body: {
            id: 'service_contact_new_contact_body',
            description: 'Please enter new contact information below:',
            defaultMessage: 'Please enter new contact information below:'
        },
        service_contact__name_required: {
            id: 'service_contact__name_required',
            description: 'Please enter a valid name',
            defaultMessage: 'Please enter a valid name'
        },
        service_contact__email_required: {
            id: 'service_contact__email_required',
            description: 'Please enter a valid email',
            defaultMessage: 'Please enter a valid email'
        },
        service_contact__phone_required: {
            id: 'service_contact__phone_required',
            description: 'Please enter a valid phone number',
            defaultMessage: 'Please enter a valid phone number'
        },
        service_contact__ext_required: {
            id: 'service_contact__ext_required',
            description: 'Please enter a valid phone extension',
            defaultMessage: 'Please enter a valid phone extension'
        },
        service_contact__required: {
            id: 'service_contact__required',
            description: 'Please select a contact for this repair',
            defaultMessage: 'Please select a contact for this repair'
        },
        service_contact__edit_contact_body: {
            id: 'service_contact__edit_contact_body',
            description: 'Shop repair contact details below. Please make any necessary changes and continue to next step:',
            defaultMessage: 'Shop repair contact details below. Please make any necessary changes and continue to next step:'
        },
        service_contact__name_sublabel: {
            id: 'service_contact__name_sublabel',
            description: '(First and Last name)',
            defaultMessage: '(First and Last name)'
        },
        service_contact__contact_label: {
            id: 'service_contact__contact_label',
            description: 'Service Contact',
            defaultMessage: 'Service Contact'
        },
        service_contact__contact_title: {
            id: 'service_contact__contact_title',
            description: 'Who should Holman contact at <u>Your Shop</u> for this service?',
            defaultMessage: 'Who should Holman contact at <u>Your Shop</u> for this service?'
        },
        service_contact__save_update: {
            id: 'service_contact__save_update',
            description: 'Confirm contact and go to the next step',
            defaultMessage: 'Confirm contact and go to the next step'
        },
        service_contact__save_update_unverified: {
            id: 'service_contact__save_update_unverified',
            description: 'Confirm and verify contact',
            defaultMessage: 'Confirm and verify contact'
        },
        service_contact__title: {
            id: 'service_contact__title',
            description: 'Service Contacts',
            defaultMessage: 'Service Contacts'
        },
        service_contact__unverified_warning: {
            id: 'service_contact__unverified_warning',
            description: 'Contact information is not verified. You will be required to verify this contact before creating a PO.',
            defaultMessage: 'Contact information is not verified. You will be required to verify this contact before creating a PO.'
        },
    });
    //#endregion
    //#region Render helpers
    //#endregion
    //#region Render
    return (React.createElement(ContactContainer, null,
        React.createElement(FormWrapper, { id: "contactViewErrorMessages", key: "contactViewErrorMessages", formErrors: [...formErrors, ...((_b = props.odometerError) === null || _b === void 0 ? void 0 : _b.some(e => e.message)) ? props.odometerError : []] }),
        React.createElement(ContactVerificationModal, { showModal: showModal, toggleShowModal: () => { setShowModal(!showModal); setOldVendorContact(emptyContact); }, vendorContact: selectedContact, vendorClient: props.vendorClient, vehicleId: vehicleId, dispatch: props.dispatch, resendDisabled: resendDisabled, resendVerificationEmail: resendVerificationEmail, userLocale: props.userLocale, emailError: sendEmailError, isCreatingPo: true, verificationId: verificationId, oldVendorContact: oldVendorContact }),
        React.createElement(Row, null,
            React.createElement(Row, { style: { fontSize: "36px", fontFamily: "Gotham SSm A", fontStyle: "normal", fontWeight: 300, color: "#5587B7", marginTop: "20px" } },
                React.createElement(FormattedMessage, Object.assign({}, messages.service_contact__title))),
            React.createElement(Row, { style: { paddingBottom: "1rem", textAlign: "start" } },
                React.createElement(Col, { md: 12 },
                    React.createElement("div", { className: "odometer_update_col__label" },
                        React.createElement(FormattedMessage, Object.assign({}, messages.service_contact__contact_title, { values: { u: (text) => React.createElement("u", null, text) } }))))),
            React.createElement(Row, null,
                React.createElement(Col, { md: 4 },
                    React.createElement("div", { className: "odometer_update_col__label" },
                        React.createElement(FormattedMessage, Object.assign({}, messages.service_contact__contact_label)))),
                React.createElement(Col, { md: 4 },
                    React.createElement(OverlayTrigger, { show: overlayContactShown, placement: "right", overlay: React.createElement(Popover, { id: "popOverContact", className: "ErrorMessage" },
                            React.createElement(FormattedMessage, Object.assign({}, messages.service_contact__required))) },
                        React.createElement(ContactSelect, { id: "contact_select_dropdown", value: selectedContact.id, onChange: (e) => onSelectContact(e) },
                            React.createElement("option", { id: "select_empty", value: "" }), contactsData === null || contactsData === void 0 ? void 0 :
                            contactsData.vendor.vendorContacts.slice().sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0)).map((c, i) => {
                                var _a;
                                return (React.createElement("option", { style: { fontWeight: "600", paddingLeft: "1rem" }, key: i, value: c.id, id: `contact_${i}` }, (_a = c.name) !== null && _a !== void 0 ? _a : c.emailAddress));
                            }),
                            React.createElement("hr", null),
                            React.createElement("option", { id: "select_new_contact", style: { color: "#5587B7", fontWeight: "500", textAlign: "center" }, value: -1 },
                                React.createElement(FormattedMessage, Object.assign({}, messages.service_contact_new_contact)),
                                "\u00A0\u00A0"))),
                    contactsLoading && React.createElement(Spinner, { animation: 'border' }))),
            selectedContact.id != undefined && selectedContact.id != "" &&
                React.createElement(Form, { id: "contactEditForm" },
                    React.createElement(Row, null,
                        React.createElement(Col, { style: { paddingTop: "2rem" } }, selectedContact.id == -1 ?
                            React.createElement(FormattedMessage, Object.assign({}, messages.service_contact_new_contact_body))
                            : React.createElement(FormattedMessage, Object.assign({}, messages.service_contact__edit_contact_body)))),
                    (selectedContact.id == -1 || selectedContact.name == null) && React.createElement(Row, { className: 'align-items-end' },
                        React.createElement(Col, { lg: 4, md: 4 },
                            React.createElement("div", { className: "odometer_update_col__label" },
                                React.createElement(FormattedMessage, Object.assign({}, messages.service_contact_name))),
                            React.createElement("div", { className: "odometer_update_col__label_optional" },
                                React.createElement(FormattedMessage, Object.assign({}, messages.service_contact__name_sublabel)))),
                        React.createElement(Col, { lg: 4, md: 4 },
                            React.createElement(OverlayTrigger, { show: ((_c = formik.errors) === null || _c === void 0 ? void 0 : _c.contactName) != undefined, placement: "right", overlay: React.createElement(Popover, { id: "popOverContactName", className: "ErrorMessage" }, ((_d = formik.errors) === null || _d === void 0 ? void 0 : _d.contactName) != undefined && React.createElement(FormattedMessage, Object.assign({}, messages[formik.errors.contactName]))) },
                                React.createElement("input", { className: "odometer_update__input-box", id: "contactName", name: "contactName", type: "text", onChange: formik.handleChange, value: formik.values.contactName, style: { width: "100%" }, maxLength: 50 })))),
                    React.createElement(Row, null,
                        React.createElement(Col, { lg: 4 },
                            React.createElement("div", { className: "odometer_update_col__label" },
                                React.createElement(FormattedMessage, Object.assign({}, messages.service_contact_phone)))),
                        React.createElement(Col, { lg: 3 },
                            React.createElement(OverlayTrigger, { show: ((_e = formik.errors) === null || _e === void 0 ? void 0 : _e.contactPhone) != undefined, placement: "right", overlay: React.createElement(Popover, { id: "popOverContactPhone", className: "ErrorMessage" }, ((_f = formik.errors) === null || _f === void 0 ? void 0 : _f.contactPhone) != undefined && React.createElement(FormattedMessage, Object.assign({}, messages[formik.errors.contactPhone]))) }, ({ ref }) => (React.createElement(PatternFormat, { className: "odometer_update__input-box", format: "(###) ### ####", allowEmptyFormatting: true, mask: "_", id: "contactPhone", name: "contactPhone", onChange: formik.handleChange, value: formik.values.contactPhone, style: { width: "100%" }, maxLength: 15, getInputRef: ref, type: 'tel' })))),
                        React.createElement(Col, { lg: { span: "1", offset: "1" } },
                            React.createElement("div", { className: "odometer_update_col__label" },
                                React.createElement(FormattedMessage, Object.assign({}, messages.service_contact_ext)))),
                        React.createElement(Col, { lg: 2 },
                            React.createElement(OverlayTrigger, { show: ((_g = formik.errors) === null || _g === void 0 ? void 0 : _g.contactExt) != undefined, placement: "right", overlay: React.createElement(Popover, { id: "popOverContactExt", className: "ErrorMessage" }, ((_h = formik.errors) === null || _h === void 0 ? void 0 : _h.contactExt) != undefined && React.createElement(FormattedMessage, Object.assign({}, messages[formik.errors.contactExt]))) },
                                React.createElement("input", { className: "odometer_update__input-box", id: "contactExt", name: "contactExt", onChange: formik.handleChange, value: formik.values.contactExt, maxLength: 7, style: { width: "100%" } })))),
                    React.createElement(Row, null,
                        React.createElement(Col, { lg: 4 },
                            React.createElement("div", { className: "odometer_update_col__label" },
                                React.createElement(FormattedMessage, Object.assign({}, messages.service_contact_email)))),
                        React.createElement(Col, { lg: 4 },
                            React.createElement(OverlayTrigger, { show: ((_j = formik.errors) === null || _j === void 0 ? void 0 : _j.contactEmail) != undefined, placement: "right", overlay: React.createElement(Popover, { id: "popOverContactEmail", className: "ErrorMessage" }, ((_k = formik.errors) === null || _k === void 0 ? void 0 : _k.contactEmail) != undefined && React.createElement(FormattedMessage, Object.assign({}, messages[formik.errors.contactEmail]))) },
                                React.createElement("input", { className: "odometer_update__input-box", id: "contactEmail", name: "contactEmail", type: "email", onChange: formik.handleChange, value: formik.values.contactEmail, style: { width: "100%" }, maxLength: 50 })))))),
        React.createElement(Row, { style: { padding: "3rem 0 2rem 0" } },
            selectedContact.id != undefined && selectedContact.id != "" && (!selectedContact.vendorEmailIsVerified || formik.values.contactEmail != selectedContact.emailAddress) &&
                React.createElement(Row, { className: "justify-content-end align-items-center d-flex", style: { color: "red", fontSize: "12px", paddingRight: "1rem" } },
                    React.createElement(FormattedMessage, Object.assign({}, messages.service_contact__unverified_warning))),
            React.createElement(Row, { className: "justify-content-end align-items-center d-flex" },
                React.createElement(ButtonStyled, { onClick: handleSubmissionClicked, id: "service_contact_submit_btn", disable: sendEmailLoading || updateLoading, style: { width: "fit-content" } },
                    React.createElement(FormattedMessage, Object.assign({}, (selectedContact.vendorEmailIsVerified ? messages.service_contact__save_update : messages.service_contact__save_update_unverified))),
                    ' ',
                    React.createElement("img", { src: "/assets/VMS_33button_go_bblue.png" })),
                (sendEmailLoading || updateLoading) && React.createElement(Spinner, { style: { height: "23px" }, animation: 'border' })))));
    //#endregion
};
const mapStateToProps = (state, props) => {
    return {
        odometerError: state.appState.getIn(['uiData', 'odometerView', 'errorMessage'])
    };
};
// #endregion
export default connect(mapStateToProps)(RepairContactView);
